import { createContext, useEffect, useState } from 'react';
import {
    Stock,
    StocksResponse,
    StockResponse,
    StockExportResponse,
    StockMovementsResponse,
    StockMovementsPageResponse,
    StockMovement,
    StockStats,
    StockCategory,
    UnitsResponse,
    Units
} from '../../models/interfaces';
import topApi from '../../components/api/ToPApi';
import { InventoryAccountingStatus } from '../../pages/stock/inventoryAccounting';


export type LoadStocksFilters = {
    search?: string;
    category?: number | string;
    status?: string;
};

// Producto eliminado del inventario que se puede reactivar
export type DeletedStock = {
    id: number;
    code: string;
    name: string;
    deletedAt: string | null;
};

export type LoadMovementsFilters = {
    search?: string;
    movementType?: string;
    startDate?: string;
    endDate?: string;
};

type StocksContextProps = {
    stocks: Stock[];
    totalPage: number;
    totalRecords: number;
    stats: StockStats;
    categories: StockCategory[];
    isLoading: boolean;
    loadStocks: (page: number, pageSize?: number, filters?: LoadStocksFilters) => Promise<void>;
    loadUnits: () => Promise<Units[]>;
    // onCreated recibe el producto creado (con su id), útil para asociarlo después
    // reactivated = true si en lugar de crearlo se reactivó un producto eliminado con el mismo código
    createStock: (stock: Stock, onCreated?: (stock: Stock, reactivated: boolean) => void) => Promise<string>;
    findDeletedStockByCode: (code: string) => Promise<DeletedStock | null>;
    // Actualiza la cantidad de los productos del menú con el stock actual del inventario
    applyInventoryToProducts: () => Promise<{ associatedProducts?: number; updatedProducts?: number; error?: string }>;
    updateStock: (stock: Stock) => Promise<string>;
    deleteStock: (id: number) => Promise<string>;
    addStock: (stock: any) => Promise<string>;
    removeStock: (stock: any) => Promise<string>;
    replaceStock: (stock: any) => Promise<string>;
    exportStocks: (filters?: LoadStocksFilters) => Promise<Stock[]>;
    loadRecentMovements: (limit?: number) => Promise<StockMovement[]>;
    loadMovements: (page: number, pageSize?: number, filters?: LoadMovementsFilters) => Promise<StockMovementsPageResponse | null>;
    // Integración del inventario con la contabilidad
    loadAccountingStatus: () => Promise<InventoryAccountingStatus | null>;
    sendInitialInventory: () => Promise<{ status?: InventoryAccountingStatus; products?: number; movements?: number; error?: string }>;
    retryAccountingErrors: () => Promise<{ status?: InventoryAccountingStatus; retried?: number; error?: string }>;
}



export const StocksContext = createContext({} as StocksContextProps);

const emptyStats: StockStats = { total: 0, inStock: 0, lowStock: 0, outOfStock: 0 };

// autoLoad=false evita cargar el inventario al montar (pantallas que solo crean productos)
export const StocksProvider = ({ children, autoLoad = true }: any) => {

    const [stocks, setStocks] = useState<Stock[]>([]);
    const [totalPage, setTotalPage] = useState<number>(0);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [stats, setStats] = useState<StockStats>(emptyStats);
    const [categories, setCategories] = useState<StockCategory[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const pageSize = 10;

    useEffect(() => {
        if (autoLoad) loadStocks(0);
    }, [])


    const loadStocks = async (pageActual: number, pageSizeParam: number = pageSize, filters: LoadStocksFilters = {}) => {
        setIsLoading(true);
        try {
            const params: any = {
                page: pageActual + 1,
                pageSize: pageSizeParam,
            };
            if (filters.search) params.search = filters.search;
            if (filters.category && filters.category !== 'all') params.category = filters.category;
            if (filters.status && filters.status !== 'all') params.status = filters.status;

            const resp = await topApi.get<StocksResponse>('/stock/getStocks', { params });
            if (resp.status == 200) {
                setStocks([...resp.data.stocks]);
                setTotalPage(resp.data.totalPages ?? 1);
                setTotalRecords(resp.data.totalRecords ?? resp.data.stocks.length);
                setStats(resp.data.stats ?? emptyStats);
                setCategories(resp.data.categories ?? []);
            }
        } finally {
            setIsLoading(false);
        }
    }


    const loadUnits = async () => {

        const resp = await topApi.get<UnitsResponse>('/stock/getUnits');
        if (resp.status == 200) {
            return resp.data.units;
        } else {
            return [];
        }
    }

    const createStock = async (stock: Stock, onCreated?: (stock: Stock, reactivated: boolean) => void) => {
        try {
            stock.id = undefined;
            const resp = await topApi.post<StockResponse>('/stock/', { stock });
            setStocks([...stocks, resp.data.stock]);
            onCreated?.(resp.data.stock, !!(resp.data as any).reactivated);
            return 'success'

        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }


    const updateStock = async (stock: Stock) => {
        try {
            const resp = await topApi.put<StockResponse>(`/stock/${stock.id}`, { stock });

            setStocks(stocks.map(prod => {
                return (prod.id === stock.id)
                    ? resp.data["stock"]
                    : prod;
            }));

            return 'success'

        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }


    }

    // Botón "+": suma una cantidad al stock actual
    const addStock = async (stock: any) => {
        try {
            const resp = await topApi.put<StockResponse>(`/stock/addStock/${stock.id}`, { stock });

            const updateStock = resp.data["stock"];

            setStocks(stocks.map(prod => {
                if (prod.id === stock.id)
                    return { ...prod, stock: updateStock.stock, status: updateStock.status };
                return prod;
            }));

            return 'success'

        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }


    }

    const removeStock = async (stock: any) => {
        try {
            const resp = await topApi.put<StockResponse>(`/stock/removeStock/${stock.id}`, { stock });

            const updateStock = resp.data["stock"];

            setStocks(stocks.map(prod => {
                if (prod.id === stock.id)
                    return { ...prod, stock: updateStock.stock, status: updateStock.status };
                return prod;
            }));

            return 'success'

        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }


    }

    // Botón "lápiz": reemplaza el stock por un valor absoluto
    const replaceStock = async (stock: any) => {
        try {
            const resp = await topApi.put<StockResponse>(`/stock/setStock/${stock.id}`, { stock });

            const updateStock = resp.data["stock"];

            setStocks(stocks.map(prod => {
                if (prod.id === stock.id)
                    return { ...prod, stock: updateStock.stock, status: updateStock.status };
                return prod;
            }));

            return 'success'

        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    const deleteStock = async (id: number) => {
        try {
            await topApi.delete<StockResponse>(`/stock/${id}`);
            setStocks((prevStocks) =>
                prevStocks.filter((stock) => stock.id! !== id)
            );
            return 'success'
        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    // Trae TODOS los registros que cumplan los filtros (sin paginar) para exportar a CSV
    const exportStocks = async (filters: LoadStocksFilters = {}) => {
        try {
            const params: any = {};
            if (filters.search) params.search = filters.search;
            if (filters.category && filters.category !== 'all') params.category = filters.category;
            if (filters.status && filters.status !== 'all') params.status = filters.status;

            const resp = await topApi.get<StockExportResponse>('/stock/exportStocks', { params });
            if (resp.status == 200) {
                return resp.data.stocks;
            }
            return [];
        } catch (e) {
            return [];
        }
    }

    const loadRecentMovements = async (limit: number = 10) => {
        try {
            const resp = await topApi.get<StockMovementsResponse>('/stock/getRecentMovements', { params: { limit } });
            if (resp.status == 200) {
                return resp.data.movements;
            }
            return [];
        } catch (e) {
            return [];
        }
    }

    const applyInventoryToProducts = async () => {
        try {
            const resp = await topApi.post<{ associatedProducts: number; updatedProducts: number }>('/stock/applyToProducts');
            return {
                associatedProducts: resp.data.associatedProducts ?? 0,
                updatedProducts: resp.data.updatedProducts ?? 0
            };
        } catch (e) {
            const error = e.response?.data?.error;
            return { error: error || "Ocurrió un error de comunicación, intente de nuevo." };
        }
    }

    const findDeletedStockByCode = async (code: string) => {
        try {
            const resp = await topApi.get<{ stock: DeletedStock | null }>('/stock/findDeletedByCode', { params: { code } });
            return resp.data.stock ?? null;
        } catch (e) {
            return null;
        }
    }

    // Bitácora completa paginada (pantalla "Ver todos" de movimientos)
    const loadMovements = async (pageActual: number, pageSizeParam: number = pageSize, filters: LoadMovementsFilters = {}) => {
        try {
            const params: any = {
                page: pageActual + 1,
                pageSize: pageSizeParam,
            };
            if (filters.search) params.search = filters.search;
            if (filters.movementType && filters.movementType !== 'all') params.movementType = filters.movementType;
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate) params.endDate = filters.endDate;

            const resp = await topApi.get<StockMovementsPageResponse>('/stock/getMovements', { params });
            if (resp.status == 200) {
                return resp.data;
            }
            return null;
        } catch (e) {
            return null;
        }
    }


    const loadAccountingStatus = async () => {
        try {
            const resp = await topApi.get<{ status: InventoryAccountingStatus }>('/stock/accounting/status');
            return resp.data.status ?? null;
        } catch (e) {
            return null;
        }
    }

    // Una sola vez: envía el inventario actual como INICIAL y activa el envío de los movimientos
    const sendInitialInventory = async () => {
        try {
            const resp = await topApi.post<{ status: InventoryAccountingStatus; products: number; movements: number }>('/stock/accounting/initial');
            return resp.data;
        } catch (e) {
            const error = e.response?.data?.error;
            return { error: error || "Ocurrió un error de comunicación, intente de nuevo." };
        }
    }

    const retryAccountingErrors = async () => {
        try {
            const resp = await topApi.post<{ status: InventoryAccountingStatus; retried: number }>('/stock/accounting/retry');
            return resp.data;
        } catch (e) {
            const error = e.response?.data?.error;
            return { error: error || "Ocurrió un error de comunicación, intente de nuevo." };
        }
    }

    return (
        <StocksContext.Provider value={{
            stocks,
            totalPage,
            totalRecords,
            stats,
            categories,
            isLoading,
            loadStocks,
            createStock,
            updateStock,
            deleteStock,
            loadUnits,
            addStock,
            removeStock,
            replaceStock,
            exportStocks,
            loadRecentMovements,
            loadMovements,
            findDeletedStockByCode,
            applyInventoryToProducts,
            loadAccountingStatus,
            sendInitialInventory,
            retryAccountingErrors
        }}>
            {children}
        </StocksContext.Provider>
    );
}
